import GridTable from '@components/home/gridTable';
import TimerCreationWizardModal, { TimerCreationWizardSelection } from '@components/templates/timerCreationWizardModal';
import WelcomeModal from '@components/welcomeModal';
import { PlusIcon } from '@icons/plus-icon';
import { useConfigs } from '@lib/hooks';
import { findTimerTemplate } from '@lib/timerTemplates';
import { Button, Card, CardBody, CardHeader, Spinner } from '@nextui-org/react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { ReactElement, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useAmplitude } from '@/amplitude/amplitudeContext';
import { AmplitudeEvent } from '@/amplitude/events';

export default function HomePage(): ReactElement {
	const router = useRouter();
	const { amplitudeInstance } = useAmplitude();
	const { configs, isLoading, mutate, error } = useConfigs();
	const [isWizardOpen, setIsWizardOpen] = useState<boolean>(false);

	useEffect(() => {
		if (error) {
			toast.error(error instanceof Error ? error.message : 'Something went wrong');
		}
	}, [error]);

	const handleCreateClick = () => {
		amplitudeInstance?.track(AmplitudeEvent.TIMER_CREATED);
		setIsWizardOpen(true);
	};

	const handleWizardSelect = ({ timerType, templateId }: TimerCreationWizardSelection) => {
		amplitudeInstance?.track(AmplitudeEvent.TEMPLATE_SELECTED, {
			template_title: findTimerTemplate(templateId)?.name ?? templateId,
		});
		setIsWizardOpen(false);
		router.push({ pathname: '/create', query: { timerType, template: templateId } });
	};

	return (
		<div className='flex flex-col min-h-screen w-full lg:w-3/4 mx-auto px-4 mt-[25px]'>
			<div className='flex-grow'>
				<Card className='p-4 rounded-xl'>
					<CardBody className='p-0'>
						<div className='flex items-center justify-between flex-col lg:flex-row gap-4'>
							<div className='flex items-center gap-3 flex-col lg:flex-row w-full lg:w-auto'>
								<Image alt='Timer settings' src='/images/timer_settings.png' width={65} height={65} />
								<div className='text-center lg:text-left'>
									<p className='font-bold text-base leading-6'>Create new</p>
									<p className='font-normal text-base leading-6 text-default-500'>
										Increase urgency and boost conversions by showing a countdown timer to your visitors.
									</p>
								</div>
							</div>
							<div className='w-full lg:w-auto'>
								<Button
									startContent={<PlusIcon />}
									color='primary'
									className='rounded-lg text-base leading-6 font-medium w-full lg:w-auto'
									onPress={handleCreateClick}
								>
									Create timer
								</Button>
							</div>
						</div>
					</CardBody>
				</Card>
				<Card className='p-4 rounded-xl mt-5'>
					<CardHeader className='p-0'>
						<p className='font-bold text-base leading-6'>Timers</p>
					</CardHeader>
					<CardBody className='p-0 mt-6'>
						{isLoading && <Spinner />}
						{!isLoading && configs && <GridTable data={configs} mutate={mutate} isEmpty={configs.length === 0} />}
					</CardBody>
				</Card>
			</div>
			<WelcomeModal />
			<TimerCreationWizardModal isOpen={isWizardOpen} onOpenChange={setIsWizardOpen} onSelect={handleWizardSelect} />
		</div>
	);
}
